import React from 'react';
import { Palette, X } from 'lucide-react';
import { TimelineSequence } from '@/components/video-editor/TimelineSequence';
import { RenderControlsPanel } from '@/components/video-editor/RenderControlsPanel';
import { TemplatesTab, type VideoTemplate } from '@/components/templates';
import type { EnhancerFile } from '@/lib/enhancerMeta';

// Timeline panel — the manual (Quick Render) editing surface: the timeline
// itself, render controls (filters/color/master prompt), and template access.

const cardStyle: React.CSSProperties = {
  padding: 18,
  borderRadius: 'var(--hj-r-md)',
  background: 'var(--hj-surface-1)',
  border: '1px solid var(--hj-hairline)',
};

interface TimelinePanelProps {
  timeline: React.ComponentProps<typeof TimelineSequence>;
  renderControls: Record<string, any>; // RenderControlsPanel's prop bag (it accepts extras)
  clipsCount: number;
  templates: {
    show: boolean;
    onClose: () => void;
    onApply: (template: VideoTemplate) => void;
    active: VideoTemplate | null;
    files: EnhancerFile[];
  };
}

export const TimelinePanel: React.FC<TimelinePanelProps> = ({ timeline, renderControls, clipsCount, templates }) => {
  return (
    <div className="flex flex-col gap-4">
      {/* Templates */}
      {templates.show && (
        <section style={cardStyle}>
          <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
            <Palette className="w-3.5 h-3.5" style={{ color: 'var(--hj-ink-3)' }} />
            <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Templates</span>
            <button
              type="button"
              onClick={templates.onClose}
              className="inline-flex items-center justify-center"
              style={{
                marginLeft: 'auto', width: 26, height: 26, borderRadius: 8,
                border: '1px solid var(--hj-hairline)', background: 'transparent',
                color: 'var(--hj-ink-2)', cursor: 'pointer',
              }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <TemplatesTab
            onApplyTemplate={templates.onApply}
            currentProjectSettings={templates.active?.settings}
            projectFiles={templates.files as any}
          />
        </section>
      )}

      {/* Timeline */}
      <section style={{ ...cardStyle, padding: 14 }}>
        <div className="mono-label" style={{ marginBottom: 10, color: 'var(--hj-ink-3)' }}>Timeline</div>
        <TimelineSequence {...timeline} />
      </section>

      {/* Render controls */}
      {clipsCount > 0 && (
        <section style={cardStyle}>
          <div className="mono-label" style={{ marginBottom: 10, color: 'var(--hj-ink-3)' }}>Output</div>
          <RenderControlsPanel {...(renderControls as any)} />
        </section>
      )}
    </div>
  );
};
